import { Controller } from "@hotwired/stimulus";
import { rollVerdict } from "../dice/feed";
import { rollKey } from "../dice/landing";
import { randomPlayerColor } from "../dice/own_color";
import type { Roll } from "../dice/pool";
import { isActivation, statBlockSpec } from "../dice/rollables";
import type { Stage } from "../dice/stage";
import { pageStage } from "../islands/character_sheet/dice_stage";
import { interpolate } from "../islands/character_sheet/labels";
import { createRoller } from "../islands/character_sheet/roller";
import type { Roller } from "../islands/ui/roll_chip";

// Connects to data-controller="page-dice" (shared/_page_dice.html.erb): the dice host of every page that is not an old one;
// see docs/architecture/frontend.md. Nothing is posted here, so every roll is the viewer's own and plays under their name.
export default class extends Controller {
	static values = { handle: String, you: String, note: String, rollTitle: String, adjustment: Object };
	declare readonly handleValue: string;
	declare readonly youValue: string;
	declare readonly noteValue: string;
	declare readonly rollTitleValue: string;
	declare readonly adjustmentValue: Record<string, string>;
	stage: Stage | null = null;
	roller: Roller | null = null;

	connect() {
		// A snapshot Turbo restores can carry the cards of a stage that no longer runs.
		for (const part of this.element.querySelectorAll('[data-dice-stage="cards"], [data-dice-stage="layer"]')) part.replaceChildren();
		this.stage = pageStage();
		this.roller = createRoller({
			url: () => null,
			subject: () => undefined,
			names: this.adjustmentValue,
			title: (spec) => interpolate(this.rollTitleValue, { description: spec.description }),
			show: ({ outcome, description }) => {
				if (outcome.kind !== "failed") this.play(outcome.roll, description);
			},
		});
	}

	disconnect() {
		this.stage?.clear();
		this.stage = null;
	}

	// The cache must not keep the cards of this visit. The host can stay connected past it, so it plays on a fresh stage.
	clear() {
		this.stage?.clear();
		this.stage = pageStage();
	}

	// The box's own Rolar: a secret roll plays like a local one, since nothing here is shared.
	played(event: Event) {
		this.play((event as CustomEvent<{ roll: Roll }>).detail.roll, "");
	}

	rollable(event: Event) {
		if (event.type === "keydown" && !isActivation((event as KeyboardEvent).key)) return;
		const target = event.target as Element | null;
		const spec = target?.closest ? statBlockSpec(target) : null;
		if (!spec) return;
		event.preventDefault();
		void this.roller?.roll(spec);
	}

	play(roll: Roll, description: string) {
		const name = this.handleValue ? `@${this.handleValue}` : this.youValue;
		const data = {
			name,
			notation: roll.notation,
			result: roll.result,
			description: description || null,
			note: this.noteValue,
			verdict: rollVerdict(roll),
			color: randomPlayerColor(),
		};
		this.stage?.show(data, { key: rollKey(data), own: true });
	}
}
